import * as Menu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

export const DropdownMenu = Menu.Root;
export const DropdownTrigger = Menu.Trigger;

export function DropdownContent({
  children,
  align = "end",
  className,
}: {
  children: ReactNode;
  align?: "start" | "end" | "center";
  className?: string;
}) {
  return (
    <Menu.Portal>
      <Menu.Content
        align={align}
        sideOffset={6}
        className={cn(
          "z-50 min-w-48 rounded-lg border border-zinc-200 bg-white p-1 shadow-lg",
          className,
        )}
      >
        {children}
      </Menu.Content>
    </Menu.Portal>
  );
}

export function DropdownItem({
  children,
  onSelect,
  danger,
  disabled,
}: {
  children: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <Menu.Item
      disabled={disabled}
      onSelect={onSelect}
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4",
        danger
          ? "text-red-600 data-[highlighted]:bg-red-50"
          : "text-zinc-700 data-[highlighted]:bg-zinc-100",
      )}
    >
      {children}
    </Menu.Item>
  );
}

export const DropdownSub = Menu.Sub;

/** Item that opens a submenu (to the right, with a chevron). */
export function DropdownSubTrigger({
  children,
  disabled,
}: {
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <Menu.SubTrigger
      disabled={disabled}
      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-zinc-700 outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-zinc-100 data-[state=open]:bg-zinc-100 [&_svg]:size-4"
    >
      {children}
      <ChevronRight className="ml-auto text-zinc-400" />
    </Menu.SubTrigger>
  );
}

export function DropdownSubContent({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <Menu.Portal>
      <Menu.SubContent
        sideOffset={4}
        alignOffset={-4}
        collisionPadding={8}
        className={cn(
          "z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-44 overflow-y-auto rounded-lg border border-zinc-200 bg-white p-1 shadow-lg",
          className,
        )}
      >
        {children}
      </Menu.SubContent>
    </Menu.Portal>
  );
}

/** Menu item with a tick when active (radio-like choices in submenus). */
export function DropdownCheckItem({
  children,
  checked,
  onSelect,
  disabled,
  keepOpen,
  className,
  title,
}: {
  children: ReactNode;
  checked: boolean;
  onSelect?: () => void;
  disabled?: boolean;
  /** Stay open after the choice (toggles inside a submenu). */
  keepOpen?: boolean;
  className?: string;
  title?: string;
}) {
  return (
    <Menu.CheckboxItem
      checked={checked}
      disabled={disabled}
      title={title}
      onSelect={(event) => {
        if (keepOpen) event.preventDefault();
        onSelect?.();
      }}
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-md py-1.5 pr-2 pl-7 text-sm text-zinc-700 outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-zinc-100 [&_svg]:size-4",
        "relative",
        className,
      )}
    >
      <Menu.ItemIndicator className="absolute left-2 inline-flex">
        <Check className="size-3.5 text-brand-600" />
      </Menu.ItemIndicator>
      {children}
    </Menu.CheckboxItem>
  );
}

export function DropdownLabel({ children }: { children: ReactNode }) {
  return (
    <Menu.Label className="px-2 py-1.5 text-xs font-medium text-zinc-500">{children}</Menu.Label>
  );
}

export function DropdownSeparator() {
  return <Menu.Separator className="my-1 h-px bg-zinc-100" />;
}
